import React, { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAuth?: boolean; // If true, requires authenticated user (not anonymous)
  fallbackPath?: string; // Where to redirect if not authenticated
}

export function ProtectedRoute({ 
  children, 
  requireAuth = false, 
  fallbackPath = '/auth' 
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated at all, redirect to auth page
  if (!isAuthenticated) {
    return (
      <Navigate 
        to={fallbackPath} 
        state={{ from: location }} 
        replace 
      />
    );
  }

  // If requireAuth is true and user is anonymous, show upgrade prompt
  if (requireAuth && user?.isAnonymous) {
    return (
      <div className="auth-required-container">
        <div className="auth-required-message">
          <h2>Account Required</h2>
          <p>This feature requires a registered account. Anonymous users cannot access this page.</p>
          <div className="auth-actions">
            <button 
              onClick={() => window.location.href = '/auth?mode=register'}
              className="primary-button"
            >
              Create Account
            </button>
            <button 
              onClick={() => window.location.href = '/auth?mode=login'}
              className="secondary-button"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  // User is authenticated and meets requirements, render children
  return <>{children}</>;
}

// Higher-order component version for class components or complex wrapping
export function withProtectedRoute<P extends object>(
  Component: React.ComponentType<P>,
  options: Omit<ProtectedRouteProps, 'children'> = {}
) {
  return function ProtectedComponent(props: P) {
    return (
      <ProtectedRoute {...options}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
}

export default ProtectedRoute;